import styles from "./AwardsCells.module.css";

type FirmLawyerCellProps = {
  /** Null means the importer found no Address Book company for this row — an
   * incomplete draft it let through on purpose, not an error. The *id* is what
   * says "matched"; `firmName` is only the label that follows from it. */
  companyId: string | null;
  firmName: string | null;
  /** Verbatim firm text from the source spreadsheet. NOT NULL upstream and kept
   * even after a match, expressly so a staff tool can show what the sheet said
   * and search the Address Book from it. */
  firmNameRaw: string;
  /** Null means either no Address Book contact matched, or the award is a
   * firm-level one with no lawyer to name. */
  contactId: string | null;
  lawyerName: string | null;
  /** Verbatim lawyer text from the source spreadsheet, on the same terms as
   * `firmNameRaw`. */
  lawyerNameRaw: string;
  /** Opens the page's Link Firm modal. Absent when the user lacks
   * `staff.awards.write` — the mark still renders, as an indicator, because a
   * read-only role has to be able to see which rows need someone else's
   * attention. The cell knows nothing about the modal itself; it only reports the
   * click. */
  onLinkFirm?: () => void;
  /** Opens the page's Link Lawyer modal, on the same terms as `onLinkFirm`.
   * Absent for a read-only user — and ignored, with a different label, on a row
   * whose firm is not linked yet: see `lawyerUnmatchedLabel`. */
  onLinkLawyer?: () => void;
};

/** Separate labels rather than one with a conditional tail, because the
 * accessible name is a promise about what the element does: a button that says
 * "link the firm first" lies about itself, and a span that says "click to link"
 * invites a click that goes nowhere. */
const FIRM_NOT_LINKED_LABEL = "Firm not linked to an Address Book company";
const FIRM_NOT_LINKED_FIXABLE_LABEL = `${FIRM_NOT_LINKED_LABEL} — click to link`;
const LAWYER_NOT_LINKED_LABEL = "Lawyer not linked to an Address Book contact";
const LAWYER_NOT_LINKED_FIXABLE_LABEL = `${LAWYER_NOT_LINKED_LABEL} — click to link`;
const LAWYER_NEEDS_FIRM_LABEL = `${LAWYER_NOT_LINKED_LABEL} — link the firm first`;

/**
 * Which of the three lawyer labels this row gets, and — read together with the
 * `onFix` below — whether its mark is a button at all.
 *
 * Permission is asked first because it is the wider fact: a user who may not
 * write gets the bare statement, with no advice about an action they could not
 * take on this row or any other. Only for someone who *can* act does the row's
 * own state become worth naming.
 *
 * `companyId === null` is the "link the firm first" case, and it is a real
 * limitation rather than a missing feature. The Link Lawyer modal searches the
 * contacts *of the linked firm*; with no firm there is no scope, and picking a
 * person by name out of the whole Address Book offers no way to check the
 * answer. The workflow is firm → lawyer, which is the importer's own order, and
 * linking the firm refetches the list — so this mark becomes a button in the
 * same sitting, on the very next render of the row.
 */
function lawyerUnmatchedLabel(companyId: string | null, onLinkLawyer?: () => void): string {
  if (!onLinkLawyer) return LAWYER_NOT_LINKED_LABEL;
  if (companyId === null) return LAWYER_NEEDS_FIRM_LABEL;
  return LAWYER_NOT_LINKED_FIXABLE_LABEL;
}

/** The broken-link glyph both forms of the mark draw. */
function NeedsLinkingIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

/**
 * The "needs linking" mark.
 *
 * Icon and idea are lifted from `pages/Research/ContentInbox/components/DetailsColumn.tsx:41-66`,
 * which draws the same broken link beside a lawyer with no `contactId` and opens
 * the link modal from it.
 *
 * With an `onClick` it is that button. Without one it stays a plain span, and
 * that is not a lesser version of the same thing: a mark that opens nothing must
 * not take focus or answer Enter with silence. Two cases still land in the
 * spanned form — either mark for a user without `staff.awards.write`, and the
 * lawyer on a row whose firm is not linked yet.
 *
 * `role="img"` + `aria-label` rather than `title` alone on the span: `title` is a
 * mouse-only affordance, and this mark is the only thing on the row saying the
 * name beside it is unresolved. Precedent: `components/ResearchCms/CmsStackedBar`.
 * The button needs no `role` — it has one — and carries the same text as its
 * accessible name.
 */
function NeedsLinkingMark({ label, onClick }: { label: string; onClick?: () => void }) {
  if (onClick) {
    return (
      <button
        type="button"
        className={`${styles.needsLinkingMark} ${styles.needsLinkingButton}`}
        aria-label={label}
        title={label}
        // The table may grow a row-level click (open the award) at any point, and
        // a fix-this-cell button that also opened the row would be a trap.
        // DetailsColumn.tsx does the same for the same reason.
        onClick={event => {
          event.stopPropagation();
          onClick();
        }}
      >
        <NeedsLinkingIcon />
      </button>
    );
  }

  return (
    <span className={styles.needsLinkingMark} role="img" aria-label={label} title={label}>
      <NeedsLinkingIcon />
    </span>
  );
}

type MatchLineProps = {
  /** The Address Book id. Its presence, not the resolved name's, is what makes
   * the line matched: the two happen to be null together today, but only one of
   * them is the thing that means "linked". */
  matchedId: string | null;
  matchedName: string | null;
  rawName: string;
  /** `.primaryLine` or `.secondaryLine` — which of the cell's two rows this is. */
  lineClassName: string;
  unmatchedLabel: string;
  /** Makes the mark a button. Absent leaves it a plain indicator. */
  onFix?: () => void;
};

function MatchLine({
  matchedId,
  matchedName,
  rawName,
  lineClassName,
  unmatchedLabel,
  onFix,
}: MatchLineProps) {
  if (matchedId !== null) {
    return <span className={lineClassName}>{matchedName || "—"}</span>;
  }

  // Nothing to quote and nothing to search the Address Book from, so the mark
  // would hang next to an empty line and the modal it opens would have no query
  // to preselect. The em dash carries the same "no value here" the rest of the
  // table uses; finding these rows is the job of the "Only incomplete" filter
  // (§10 M2), not of a badge attached to blank space.
  if (rawName.trim() === "") {
    return <span className={lineClassName}>—</span>;
  }

  return (
    <span className={lineClassName}>
      <span className={styles.unmatchedName}>{rawName}</span>
      <NeedsLinkingMark label={unmatchedLabel} onClick={onFix} />
    </span>
  );
}

/**
 * "Firm / Lawyer" — the firm on top, the lawyer underneath.
 *
 * Both lines always render: each degrades rather than disappearing, because
 * "this row has no firm" is exactly what the match-fixing round has to be able
 * to spot by scanning the column.
 *
 * A line with no id behind it shows the verbatim spreadsheet text instead of the
 * resolved name, de-emphasised and marked "needs linking" (§10). An empty cell
 * would not say there is anything to fix, and the raw text is what both modals
 * search the Address Book with.
 *
 * Both marks are controls now, but not unconditionally and not on the same
 * terms: the firm mark needs only the write permission, the lawyer mark needs
 * that *and* a firm to scope its search to.
 */
export function FirmLawyerCell({
  companyId,
  firmName,
  firmNameRaw,
  contactId,
  lawyerName,
  lawyerNameRaw,
  onLinkFirm,
  onLinkLawyer,
}: FirmLawyerCellProps) {
  return (
    <div className={styles.twoLineCell}>
      <MatchLine
        matchedId={companyId}
        matchedName={firmName}
        rawName={firmNameRaw}
        lineClassName={styles.primaryLine}
        unmatchedLabel={onLinkFirm ? FIRM_NOT_LINKED_FIXABLE_LABEL : FIRM_NOT_LINKED_LABEL}
        onFix={onLinkFirm}
      />
      <MatchLine
        matchedId={contactId}
        matchedName={lawyerName}
        rawName={lawyerNameRaw}
        lineClassName={styles.secondaryLine}
        unmatchedLabel={lawyerUnmatchedLabel(companyId, onLinkLawyer)}
        // The one place the two lines are not independent: with no company there
        // is no scope for the lawyer search, so the handler is dropped here even
        // though the page supplied one. Deciding it in the cell keeps `columns`
        // from having to know why the modal needs a firm — the cell already holds
        // `companyId`, and the label above is derived from the same condition, so
        // the two can never disagree.
        onFix={companyId !== null ? onLinkLawyer : undefined}
      />
    </div>
  );
}
